import * as React from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useOnClickOutside } from 'usehooks-ts';
import { useTranslation } from 'next-i18next';
import { TrashIcon } from '@heroicons/react/24/solid';
import {
  ArrowPathIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChevronUpDownIcon,
  NoSymbolIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  TrophyIcon,
  EyeSlashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';

import {
  Dropdown,
  DropdownItem,
  Tooltip,
  Button,
  IconButton,
  showToast,
  Link,
  RadioGroup,
  IconComponentType,
} from '@restworld/ui-ds-base';
import { JobApplicationStatusKey } from '@restworld/utility-types';
import { useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import {
  candidateModalVariantAtom,
  candidateStateChangeAtom,
  candidateStateChangeSelectionAtom,
  StatusKey,
  selectedJobApplicationAtom,
} from '../../../employer-job-listing.atoms';
import { useIsSpontaneousCandidate } from './candidate-modal';
import { useUpdateJobApplicationMutation } from '../../../hooks/use-update-job-application';
import { useEmployerJobApplicationsInfiniteQueryKey } from '../../../hooks/use-employer-job-applications';
import {
  useCandidateStateChangeModal,
  employerJobApplicationsInfiniteQueryParamsAtom,
} from '../../../employer-job-listing.atoms';

type EvaluationDropdownOption = {
  matched_status_key: (JobApplicationStatusKey | '')[];
  options: {
    key: StatusKey;
    label: string;
    status_key?: JobApplicationStatusKey;
  }[];
  Icon?: IconComponentType;
  label: string;
  key: 'evaluate_candidate' | 'manage_candidate' | 'manage_through_rw_manager' | 'recover_candidate';
}[];
export const isCandidateStatusChangeDropdownOpenAtom = atom(false);

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ICandidateStatusChangeDropdownProps {}
export const CandidateStatusChangeDropdown: React.FunctionComponent<ICandidateStatusChangeDropdownProps> = (props) => {
  const { t } = useTranslation('job-positions-page');
  const candidateStateChange = useAtomValue(candidateStateChangeAtom);
  const [toggleDropdown, setToggleDropdown] = useAtom(isCandidateStatusChangeDropdownOpenAtom);
  const [, setCandidateStateChangeSelection] = useAtom(candidateStateChangeSelectionAtom);
  const setCandidateModalVariant = useSetAtom(candidateModalVariantAtom);
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const { moveCandidateToNextStage } = useMoveCandidateToNextStage();

  const dropdownOption = useEvaluationDropdownOption();

  const matchedCandidateState = React.useMemo(
    () => dropdownOption.find(({ matched_status_key }) => matched_status_key.includes(candidateStateChange)),
    [candidateStateChange, dropdownOption]
  );

  const label = React.useMemo(() => matchedCandidateState?.label, [matchedCandidateState?.label]);
  const optionKey = React.useMemo(() => matchedCandidateState?.key, [matchedCandidateState?.key]);
  const { dropdownItems } = useCandidateMoveOptions();
  const isSpontaneousApplication = React.useMemo(
    () => !selectedJobApplication?.job_position_id,
    [selectedJobApplication?.job_position_id]
  );
  const { isSpontaneousCandidate } = useIsSpontaneousCandidate();

  React.useEffect(() => {
    setCandidateStateChangeSelection(candidateStateChange);
  }, [candidateStateChange, setCandidateStateChangeSelection]);
  const selectWrapperRef = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(
    selectWrapperRef,
    (e) => {
      e.target['type'] !== 'button' && setToggleDropdown((p) => !p);
    },
    'mousedown'
  );

  const isHired = React.useMemo(
    () =>
      selectedJobApplication?.status_key === 'worker_jp_hired' ||
      selectedJobApplication?.status_key === 'worker_jp_scheduled_hiring',
    [selectedJobApplication?.status_key]
  );

  const recoverCandidateCtaEl = React.useMemo(
    () => (
      <div className="relative p-4 lg:pt-0 lg:px-0 border-t lg:border-0 border-neutral-40">
        <Button
          onClick={() => {
            if (optionKey === 'recover_candidate') {
              setCandidateModalVariant('worker_jp_recovery_confirmation');
              setCandidateStateChangeSelection('worker_jp_employer_interested');
            }
          }}
          fullwidth
        >
          {label}
        </Button>
        {toggleDropdown && <CustomDropdown />}
      </div>
    ),
    [label, toggleDropdown, optionKey, setCandidateModalVariant, setCandidateStateChangeSelection]
  );

  const onDiscardClick = React.useCallback(() => {
    setCandidateStateChangeSelection('worker_jp_discarded');
    setCandidateModalVariant('worker_jp_discarded_confirmation');
  }, [setCandidateModalVariant, setCandidateStateChangeSelection]);

  const onHideClick = React.useCallback(() => {
    setCandidateModalVariant('hide_candidate_confirmation');
  }, [setCandidateModalVariant]);

  // "Non idonei" AI-rejected candidates (discarded by AI before reaching the shortlist):
  // show the same sticky action bar as a shortlisted candidate, but swap "Discard" for "Hide"
  // since discarding an already-discarded candidate is meaningless.
  const isNonIdoneo = React.useMemo(
    () =>
      !isSpontaneousApplication &&
      selectedJobApplication?.status_key === 'worker_jp_discarded' &&
      !selectedJobApplication?.added_to_shortlist_at,
    [isSpontaneousApplication, selectedJobApplication?.status_key, selectedJobApplication?.added_to_shortlist_at]
  );

  const defaultCtaEl = React.useMemo(() => {
    if (isSpontaneousCandidate || isSpontaneousApplication) {
      return (
        <div className="flex items-center h-fit gap-2 relative py-4 px-0 w-full lg:pt-0 lg:px-0 lg:pb-4 lg:flex-1 lg:justify-end lg:w-fit">
          <ToggleCandidateVisibilityButton />
        </div>
      );
    }

    return (
      <div className="flex items-center h-fit gap-2 relative py-4 px-0 w-full lg:pt-0 lg:px-0 lg:pb-4 lg:flex-1">
        <div className="bg-fluo-100 border border-fluo-500 rounded-lg h-12 w-full flex items-center flex-1">
          {!isHired && (
            <Button EndIcon={ArrowRightIcon} fullwidth onClick={moveCandidateToNextStage}>
              {t(
                selectedJobApplication.status_key === 'worker_jp_in_shortlist' ||
                  selectedJobApplication?.status_key === 'worker_jp_viewed_by_employer' ||
                  isNonIdoneo
                  ? 'i_am_interested'
                  : 'continue'
              )}
            </Button>
          )}
          <Dropdown
            wrapperStyle={isHired ? 'w-full' : 'mx-1'}
            title={`${t('move_to')}:`}
            placement="top-end"
            items={dropdownItems}
            triggerElement={
              isHired ? (
                <Button EndIcon={ChevronUpDownIcon} fullwidth>
                  {t('manage')}
                </Button>
              ) : (
                <IconButton ariaLabel={t('move_to_step')} Icon={ChevronUpDownIcon} variant="restworld" />
              )
            }
          />
        </div>

        <div className="max-w-fit flex items-center justify-end flex-1">
          <div className="md:hidden">
            <ThirdActionTooltip isNonIdoneo={isNonIdoneo}>
              <Button
                variant={isNonIdoneo ? 'secondary-neutral' : 'secondary-danger'}
                size="md"
                onClick={isNonIdoneo ? onHideClick : onDiscardClick}
                StartIcon={isNonIdoneo ? EyeSlashIcon : NoSymbolIcon}
                className={'min-w-fit'}
              />
            </ThirdActionTooltip>
          </div>
          <div className="hidden md:flex">
            <ThirdActionTooltip isNonIdoneo={isNonIdoneo}>
              <Button
                variant={isNonIdoneo ? 'secondary-neutral' : 'secondary-danger'}
                size="md"
                onClick={isNonIdoneo ? onHideClick : onDiscardClick}
                StartIcon={isNonIdoneo ? EyeSlashIcon : NoSymbolIcon}
                className={'min-w-fit'}
              >
                {t(isNonIdoneo ? 'hide_candidate' : 'discard_candidate')}
              </Button>
            </ThirdActionTooltip>
          </div>
        </div>
      </div>
    );
  }, [
    isSpontaneousCandidate,
    isSpontaneousApplication,
    isHired,
    isNonIdoneo,
    moveCandidateToNextStage,
    t,
    selectedJobApplication.status_key,
    dropdownItems,
    onDiscardClick,
    onHideClick,
  ]);

  if (isNonIdoneo) return defaultCtaEl;
  return selectedJobApplication?.status_key === 'worker_jp_discarded' && !isSpontaneousApplication
    ? recoverCandidateCtaEl
    : defaultCtaEl;
};

export default CandidateStatusChangeDropdown;

function ToggleCandidateVisibilityButton() {
  const { t } = useTranslation('spontaneous-candidates-page');
  const { isPending, isHidden, handleClick } = useToggleCandidateVisibilityButton();

  return (
    <Button
      StartIcon={isHidden ? EyeIcon : EyeSlashIcon}
      variant="secondary-danger"
      onClick={handleClick}
      loading={isPending}
    >
      {isHidden ? t('recover_candidate') : t('hide_candidate')}
    </Button>
  );
}

function useToggleCandidateVisibilityButton() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const isHidden = selectedJobApplication?.hidden;
  const { mutate, isPending, isSuccess } = useUpdateJobApplicationMutation();
  const employerJobApplicationsInfiniteQueryParams = useAtomValue(employerJobApplicationsInfiniteQueryParamsAtom);
  const queryKey = useEmployerJobApplicationsInfiniteQueryKey(employerJobApplicationsInfiniteQueryParams);
  const { closeCandidateModal } = useCandidateStateChangeModal();

  const workerId = React.useMemo(
    () => selectedJobApplication?.worker?.user_id,
    [selectedJobApplication?.worker?.user_id]
  );
  const restaurantId = React.useMemo(
    () => selectedJobApplication?.restaurant?.id,
    [selectedJobApplication?.restaurant?.id]
  );

  const handleClick = React.useCallback(() => {
    mutate({
      params: { hidden: !isHidden },
      workerId,
      restaurantId,
    });
  }, [isHidden, mutate, restaurantId, workerId]);

  React.useEffect(() => {
    if (!isSuccess) return;
    queryClient.invalidateQueries({ queryKey });
    showToast(t('candidate_visibility_updated'), 'success', { autoClose: 3000, position: 'top-right' });
    closeCandidateModal();
  }, [closeCandidateModal, isSuccess, queryClient, queryKey, t]);

  return { isPending, isHidden, handleClick };
}

function ThirdActionTooltip({ children, isNonIdoneo }: React.PropsWithChildren<{ isNonIdoneo: boolean }>) {
  const { t } = useTranslation('job-positions-page');

  return <Tooltip content={t(isNonIdoneo ? 'hide_candidate' : 'discard_candidate')}>{children}</Tooltip>;
}

function useMoveCandidateToNextStage() {
  const candidateStateChange = useAtomValue(candidateStateChangeAtom);
  const [, setCandidateStateChangeSelection] = useAtom(candidateStateChangeSelectionAtom);
  const setCandidateModalVariant = useSetAtom(candidateModalVariantAtom);

  const moveCandidateToNextStage = React.useCallback(() => {
    switch (candidateStateChange) {
      case 'worker_jp_in_shortlist':
      case 'worker_jp_discarded':
        setCandidateStateChangeSelection('worker_jp_employer_interested');
        setCandidateModalVariant('state_change_confirmation');
        break;
      case 'worker_jp_employer_interested':
        setCandidateStateChangeSelection('worker_jp_interview_scheduled');
        setCandidateModalVariant('worker_jp_interview_scheduled_confirmation');
        break;
      case 'worker_jp_interview_scheduled':
      case 'worker_jp_interview_done':
        setCandidateStateChangeSelection('worker_jp_stage');
        setCandidateModalVariant('state_change_confirmation');
        break;
      case 'worker_jp_stage_scheduled':
      case 'worker_jp_stage':
      case 'worker_jp_stage_done':
        setCandidateStateChangeSelection('worker_jp_hired');
        setCandidateModalVariant('state_change_confirmation');
        break;
    }
  }, [candidateStateChange, setCandidateModalVariant, setCandidateStateChangeSelection]);

  return { moveCandidateToNextStage };
}

function useCandidateMoveOptions() {
  const { t } = useTranslation('job-positions-page');
  const [, setCandidateStateChangeSelection] = useAtom(candidateStateChangeSelectionAtom);
  const setCandidateModalVariant = useSetAtom(candidateModalVariantAtom);
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);

  const dropdownItems: DropdownItem[] = React.useMemo(
    () => [
      {
        label: t('candidates_to_be_called'),
        Icon: DevicePhoneMobileIcon,
        onClick: () => {
          setCandidateStateChangeSelection('worker_jp_employer_interested');
          setCandidateModalVariant('state_change_confirmation');
        },
        disabled: selectedJobApplication?.status_key === 'worker_jp_employer_interested',
        isSelected: selectedJobApplication?.status_key === 'worker_jp_employer_interested',
      },
      {
        label: t('candidates_to_be_interviewed'),
        Icon: UserGroupIcon,
        onClick: () => {
          setCandidateStateChangeSelection('worker_jp_interview_scheduled');
          setCandidateModalVariant('worker_jp_interview_scheduled_confirmation');
        },
        disabled:
          selectedJobApplication?.status_key === 'worker_jp_interview_scheduled' ||
          selectedJobApplication?.status_key === 'worker_jp_interview_done',
        isSelected:
          selectedJobApplication?.status_key === 'worker_jp_interview_scheduled' ||
          selectedJobApplication?.status_key === 'worker_jp_interview_done',
      },
      {
        label: t('candidates_in_stage_or_negotiation'),
        Icon: ClipboardDocumentCheckIcon,
        onClick: () => {
          setCandidateStateChangeSelection('worker_jp_stage');
          setCandidateModalVariant('state_change_confirmation');
        },
        disabled:
          selectedJobApplication?.status_key === 'worker_jp_stage_scheduled' ||
          selectedJobApplication?.status_key === 'worker_jp_stage' ||
          selectedJobApplication?.status_key === 'worker_jp_stage_done',
        isSelected:
          selectedJobApplication?.status_key === 'worker_jp_stage_scheduled' ||
          selectedJobApplication?.status_key === 'worker_jp_stage' ||
          selectedJobApplication?.status_key === 'worker_jp_stage_done',
      },
      {
        label: t('hired'),
        Icon: TrophyIcon,
        onClick: () => {
          setCandidateStateChangeSelection('worker_jp_hired');
          setCandidateModalVariant('state_change_confirmation');
        },
        disabled:
          selectedJobApplication?.status_key === 'worker_jp_hired' ||
          selectedJobApplication?.status_key === 'worker_jp_scheduled_hiring',
        isSelected:
          selectedJobApplication?.status_key === 'worker_jp_hired' ||
          selectedJobApplication?.status_key === 'worker_jp_scheduled_hiring',
      },
    ],
    [selectedJobApplication?.status_key, setCandidateModalVariant, setCandidateStateChangeSelection, t]
  );

  return { dropdownItems };
}

const CustomDropdown = () => {
  const { trackEvent } = useAnalytics();
  const [candidateStateChangeSelection, setCandidateStateChangeSelection] = useAtom(candidateStateChangeSelectionAtom);
  const { user } = useAuth();
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);

  const candidateStateChange = useAtomValue(candidateStateChangeAtom);
  const [, setToggleDropdown] = useAtom(isCandidateStatusChangeDropdownOpenAtom);
  const setCandidateModalVariant = useSetAtom(candidateModalVariantAtom);

  const selectWrapperRef = React.useRef<HTMLDivElement>(null);
  const dropdownOption = useEvaluationDropdownOption();

  const handleChange = React.useCallback(
    (value: StatusKey) => {
      setCandidateStateChangeSelection(value);
      if (value === 'worker_jp_interview_scheduled') {
        trackEvent('employer_schedule_interview', {
          email: user?.email,
          phone: user?.phone_number,
          jp_id: selectedJobApplication?.job_position_id,
          restaurant_name: selectedJobApplication?.restaurant?.restaurant_name,
        });
        setCandidateModalVariant('worker_jp_interview_scheduled_confirmation');
      }

      if (value === 'worker_jp_employer_interested') {
        setCandidateModalVariant('state_change_confirmation');
      }
      setToggleDropdown(false);
    },
    [
      setCandidateStateChangeSelection,
      setToggleDropdown,
      trackEvent,
      user?.email,
      user?.phone_number,
      selectedJobApplication?.job_position_id,
      selectedJobApplication?.restaurant?.restaurant_name,
      setCandidateModalVariant,
    ]
  );
  // useOnClickOutside(
  //   selectWrapperRef,
  //   (e) => {
  //     e.stopPropagation();
  //     e.target['type'] !== 'button' &&
  //       e.target['id'] !== 'button-label' &&
  //       e.target['id'] !== 'button-icon' &&
  //       !e.target?.['id']?.includes('radiogroup-label') &&
  //       !(typeof e.target?.['className'] === 'string' && e.target?.['className']?.includes('formselect-radio')) &&
  //       setToggleDropdown((p) => !p);
  //   },
  //   'mousedown'
  // );

  const { t } = useTranslation('job-positions-page');

  return (
    <div
      id="dropdown"
      ref={selectWrapperRef}
      className="absolute bottom-16.25 lg:w-full h-fit p-8 bg-white rounded-lg shadow border border-neutral-40"
    >
      {dropdownOption.map(({ matched_status_key, options }) => {
        if (candidateStateChange !== '' && matched_status_key.includes(candidateStateChange)) {
          return (
            <RadioGroup
              borderless
              key={matched_status_key.join('')}
              id={matched_status_key.join('')}
              options={options.map(({ key, label }) => ({ id: key, key, label }))}
              value={candidateStateChangeSelection}
              onChange={handleChange}
            />
          );
        }
        return null;
      })}
      <Link
        className={'mt-2 ml-1'}
        StartIcon={TrashIcon}
        variant="error"
        onClick={() => {
          trackEvent('employer_discard_candidate', {
            email: user?.email,
            phone: user?.phone_number,
            jp_id: selectedJobApplication?.job_position_id,
            restaurant_name: selectedJobApplication?.restaurant?.restaurant_name,
          });
          setCandidateStateChangeSelection('worker_jp_discarded');
          setCandidateModalVariant('worker_jp_discarded_confirmation');
          setToggleDropdown(false);
        }}
        newTab={false}
      >
        {t('discard_candidate')}
      </Link>
    </div>
  );
};
const useEvaluationDropdownOption = () => {
  const { t } = useTranslation('job-positions-page');
  const dropdownOption: EvaluationDropdownOption = React.useMemo(
    () => [
      {
        matched_status_key: ['worker_jp_in_shortlist', 'worker_jp_viewed_by_employer'],
        options: [
          { key: 'worker_jp_in_shortlist', label: t('qualified') || '' },
          {
            key: 'worker_jp_employer_interested',
            label: t('to_be_called_on_the_phone'),
          },
        ],
        label: t('evaluate_candidate'),
        key: 'evaluate_candidate',
        Icon: ChevronUpDownIcon,
      },
      {
        matched_status_key: ['worker_jp_employer_interested'],
        options: [
          {
            key: 'worker_jp_employer_interested',
            label: t('waiting_for_a_call'),
          },
          {
            key: 'worker_jp_interview_scheduled',
            label: t('interview_scheduled_dropdown_title'),
          },
        ],
        Icon: ChevronUpDownIcon,
        label: t('manage_candidate'),
        key: 'manage_candidate',
      },
      {
        matched_status_key: [
          'worker_jp_interview_scheduled',
          'worker_jp_interview_done',
          'worker_jp_stage_scheduled',
          'worker_jp_stage',
          'worker_jp_stage_done',
          'worker_jp_hired',
          'worker_jp_scheduled_hiring',
        ],
        options: [],
        label: t('manage_through_rw_manager'),
        Icon: ChatBubbleOvalLeftEllipsisIcon,
        key: 'manage_through_rw_manager',
      },
      {
        matched_status_key: ['worker_jp_discarded'],
        options: [],
        label: t('recover_candidate'),
        icon: ArrowPathIcon,
        key: 'recover_candidate',
      },
    ],
    [t]
  );
  return dropdownOption;
};
